import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { syncThemeWithLocal } from "./helpers/theme-helper";
import { useTranslation } from "react-i18next";
import "./localization/i18n";
import { updateAppLanguage } from "./helpers/language-helper";
import { router } from "./routes/router";
import { RouterProvider } from "@tanstack/react-router";
import LoadingScreen from "./components/LoadingScreen";
import { EngagementProvider } from "./contexts/EngagementContext";
import { AuthProvider } from "./contexts/AuthContext";
import { SharePointConfigProvider } from "./contexts/SharePointConfigContext";
import { InactivityTimeoutProvider } from "./contexts/InactivityTimeoutContext";
import { WebArchiveGate } from "./web-archive/WebArchiveGate";

export default function App() {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Loading Verity...");

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Hide the HTML splash screen
        const splashScreen = document.getElementById('splash-screen');
        if (splashScreen) {
          splashScreen.style.opacity = '0';
          splashScreen.style.transition = 'opacity 0.5s ease-out';
          setTimeout(() => {
            splashScreen.style.display = 'none';
          }, 500);
        }

        // Force dark theme initially to prevent white background flash
        document.documentElement.classList.add("dark");

        // Show loading with different messages
        setLoadingMessage("Initializing theme...");
        await new Promise(resolve => setTimeout(resolve, 300));

        setLoadingMessage("Loading language settings...");
        await new Promise(resolve => setTimeout(resolve, 300));

        setLoadingMessage("Setting up application...");
        await new Promise(resolve => setTimeout(resolve, 300));

        await syncThemeWithLocal();
        updateAppLanguage(i18n);

        setLoadingMessage("Finalizing setup...");
        await new Promise(resolve => setTimeout(resolve, 500));

        // Hide loading screen
        setIsLoading(false);
      } catch (error) {
        console.error("Error during app initialization:", error);
        setIsLoading(false);
      }
    };

    initializeApp();
  }, [i18n]);

  if (isLoading) {
    return (
      <WebArchiveGate>
        <LoadingScreen message={loadingMessage} />
      </WebArchiveGate>
    );
  }

  const appTree = (
    <AuthProvider>
      <InactivityTimeoutProvider>
        <SharePointConfigProvider>
          <EngagementProvider>
            <RouterProvider router={router} />
          </EngagementProvider>
        </SharePointConfigProvider>
      </InactivityTimeoutProvider>
    </AuthProvider>
  );

  return <WebArchiveGate>{appTree}</WebArchiveGate>;
}

// Global error handlers for unhandled errors and promise rejections
window.addEventListener('error', (event) => {
  const error = event.error || new Error(event.message || 'Unknown error');

  console.error('========== Unhandled Error ==========');
  console.error('Message:', error.message);
  console.error('Stack:', error.stack);
  console.error('Filename:', event.filename);
  console.error('Line:', event.lineno);
  console.error('Column:', event.colno);
  console.error('=====================================');

  // Send to main process for terminal logging
  if (window.electronWindow && typeof window.electronWindow.logError === 'function') {
    try {
      window.electronWindow.logError({
        message: error.message,
        stack: error.stack,
        errorType: 'Unhandled Error',
        location: `${event.filename}:${event.lineno}:${event.colno}`,
      }).catch((err: any) => {
        console.error('Failed to log error to main process:', err);
      });
    } catch (logErr) {
      console.error('Error calling logError:', logErr);
    }
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason instanceof Error
    ? event.reason
    : new Error(String(event.reason || 'Unhandled promise rejection'));

  console.error('========== Unhandled Promise Rejection ==========');
  console.error('Reason:', error.message);
  console.error('Stack:', error.stack);
  console.error('=================================================');

  // Send to main process for terminal logging
  if (window.electronWindow && typeof window.electronWindow.logError === 'function') {
    try {
      window.electronWindow.logError({
        message: `Unhandled Promise Rejection: ${error.message}`,
        stack: error.stack,
        errorType: 'Unhandled Promise Rejection',
        location: 'Promise chain',
      }).catch((err: any) => {
        console.error('Failed to log error to main process:', err);
      });
    } catch (logErr) {
      console.error('Error calling logError:', logErr);
    }
  }
});

const root = createRoot(document.getElementById("app")!);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
